import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';

/**
 * Application routes configuration with lazy loading
 * All feature components are loaded on-demand to optimize initial bundle size
 * Protected routes use authGuard to ensure user is authenticated
 */
export const routes: Routes = [
  // Root redirect
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },

  // Public routes
  {
    path: 'login',
    loadComponent: () => import('@features/auth/login/login.component').then(m => m.LoginComponent),
    title: 'Login - QA Agent'
  },

  // Protected routes - require authentication
  {
    path: 'dashboard',
    loadComponent: () => import('@features/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [authGuard],
    title: 'Dashboard - QA Agent',
    data: { preload: true } // Preload dashboard for fast access
  },
  {
    path: 'upload',
    loadComponent: () => import('@features/upload/file-upload.component').then(m => m.FileUploadComponent),
    canActivate: [authGuard],
    title: 'Upload File - QA Agent',
    data: { preload: true }
  },

  // Rules management routes
  {
    path: 'rules',
    canActivate: [authGuard],
    data: { preload: true },
    children: [
      {
        path: '',
        loadComponent: () => import('@features/rules/rule-management/rule-management.component').then(m => m.RuleManagementComponent),
        title: 'Validation Rules - QA Agent'
      },
      {
        path: 'builder',
        loadComponent: () => import('@features/rules/wizard-container/wizard-container.component').then(m => m.WizardContainerComponent),
        title: 'Rule Builder - QA Agent'
      },
      {
        path: 'builder/:id',
        loadComponent: () => import('@features/rules/wizard-container/wizard-container.component').then(m => m.WizardContainerComponent),
        title: 'Edit Rule - QA Agent'
      }
    ]
  },

  // Validation routes
  {
    path: 'validation',
    canActivate: [authGuard],
    children: [
      {
        path: ':sessionId',
        loadComponent: () => import('@features/validation/validation-report/validation-report.component').then(m => m.ValidationReportComponent),
        title: 'Validation Report - QA Agent'
      }
    ]
  },
  {
    path: 'history',
    loadComponent: () => import('@features/validation/validation-history/validation-history.component').then(m => m.ValidationHistoryComponent),
    canActivate: [authGuard],
    title: 'Validation History - QA Agent',
    data: { preload: true, delay: 2000 } // Preload after 2 seconds
  },
  {
    path: 'comparison',
    loadComponent: () => import('@features/validation/validation-comparison/validation-comparison.component').then(m => m.ValidationComparisonComponent),
    canActivate: [authGuard],
    title: 'Validation Comparison - QA Agent'
  },

  // Audit Profiles routes
  {
    path: 'profiles',
    loadComponent: () => import('./features/profiles/profile-list.component').then(m => m.ProfileListComponent),
    canActivate: [authGuard],
    title: 'Audit Profiles - QA Agent'
  },
  {
    path: 'profiles/:id',
    loadComponent: () => import('./features/profiles/profile-detail.component').then(m => m.ProfileDetailComponent),
    canActivate: [authGuard],
    title: 'Profile Detail - QA Agent'
  },

  // AI Suggestions route
  {
    path: 'suggestions',
    loadComponent: () => import('@features/ai-suggestions/ai-suggestions.component').then(m => m.AiSuggestionsComponent),
    canActivate: [authGuard],
    title: 'AI Suggestions - QA Agent'
  },

  // Help page
  {
    path: 'help',
    loadComponent: () => import('@features/help/help.component').then(m => m.HelpComponent),
    canActivate: [authGuard],
    title: 'Help & Documentation - QA Agent'
  },

  // Fallback route - 404 Not Found
  {
    path: '**',
    loadComponent: () => import('@shared/components/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'Page Not Found - QA Agent'
  }
];
